import { Injectable } from '@angular/core';
import { Platform } from 'ionic-angular';

/**
 * Faixa (px), a partir de cada borda lateral, onde um toque é capturado (preventDefault no
 * touchstart). O touchstart é o único momento em que o Safari/Chrome do iOS ainda pode ser
 * impedido de iniciar o swipe nativo de voltar/avançar — depois que ele começa, não há API para
 * cancelar.
 *
 * Mantida estreita porque o toque capturado deixa de rolar a tela. O toque simples continua
 * funcionando: quando o gesto termina sem virar arrasto, o clique é reemitido em replayTap().
 */
const EDGE_BLOCK_ZONE_PX: number = 20;

/** Deslocamento máximo (px) para o gesto ainda ser considerado toque simples, e não arrasto. */
const TAP_SLOP_PX: number = 8;

/** Duração máxima (ms) de um toque simples. Acima disso é press/arrasto e o clique não é reemitido. */
const TAP_MAX_DURATION_MS: number = 500;

/** Solta o estado do gesto após esse tempo sem eventos — guarda para quando o touchend não chega. */
const GESTURE_INACTIVITY_TIMEOUT_MS: number = 1000;

/**
 * Componentes que tratam o próprio arrasto (horizontal, zoom ou abertura do menu lateral). Um
 * toque que começa neles é liberado: bloquear atrapalharia o gesto legítimo, e o swipe nativo
 * dificilmente é o que o usuário quis ali.
 */
const GESTURE_OWNER_SELECTOR: string = [
  'ion-slides',
  '.swiper-container',
  '.swiper-slide',
  'ion-scroll',
  'ion-item-sliding',
  'ion-menu',
  'ion-range',
  'ion-toggle',
  'ion-refresher',
  'ion-segment',
  'ion-reorder',
  'pinch-zoom',
].join(', ');

/** Campos que precisam de foco explícito ao reemitir o toque — click() sozinho não abre o teclado. */
const FOCUSABLE_SELECTOR: string = 'input, textarea, select';

/**
 * Ameniza o conflito entre o swipe-back NATIVO do navegador no iOS (Safari/Chrome, que é o mesmo
 * WKWebView) e o voltar do próprio Ionic.
 *
 * O problema: com as páginas registradas em `@IonicPage({ segment })`, o DeepLinker empurra o
 * histórico do navegador a cada push. Um arrasto na borda esquerda dispara o voltar nativo, que
 * vira `popstate` e faz o Ionic dar `pop()` — e o mesmo arrasto ainda acionava o swipe-back do
 * `ion-nav`, saindo duas telas de uma vez. Por isso o `swipeBackEnabled` do app.html hoje só está
 * ligado no Cordova: no web o voltar tem uma origem só.
 *
 * O que sobra é o gesto acidental — o dedo que encosta na borda e volta uma tela sem querer. O
 * navegador só decide iniciar o gesto nativo no touchstart; capturando esse touchstart numa faixa
 * estreita da borda e chamando preventDefault, ele não começa. Não é garantido em 100% dos casos
 * (não existe API para desligar o gesto nativo), por isso "amenizar" — mas cobre a maior parte dos
 * disparos acidentais.
 *
 * Por que não vira zona morta: preventDefault no touchstart também mata o clique sintético do
 * WebKit, o que deixaria botões e itens de lista sem resposta nos 20px da borda. Por isso o gesto
 * é acompanhado até o fim e, se tiver sido um toque simples, o clique é reemitido no alvo original.
 *
 * Os gestos do Ionic continuam funcionando dentro da faixa (arrasto do `ion-menu`, e o swipe-back
 * do `ion-nav` no build nativo): os listeners daqui só chamam preventDefault, nunca
 * stopPropagation, então o Ionic continua recebendo os mesmos eventos de toque.
 *
 * Não faz nada no app nativo (Cordova, onde não existe navegador) nem fora do iOS. Todos os
 * listeners são removidos em destroy().
 */
@Injectable()
export class IosSwipeBackService {
  private initialized: boolean = false;
  private teardownCallbacks: Array<() => void> = [];

  constructor(private platform: Platform) {}

  /** Liga o guarda de borda. Seguro chamar mais de uma vez; as chamadas seguintes são ignoradas. */
  initialize(): void {
    if (this.initialized || !this.canSetupIosSwipeBack()) {
      return;
    }
    this.initialized = true;

    this.injectStyles();
    this.setupEdgeTouchGuard();
  }

  /** Remove todo listener e elemento que este service adicionou. */
  destroy(): void {
    const callbacks: Array<() => void> = this.teardownCallbacks.splice(0).reverse();
    for (const teardown of callbacks) {
      teardown();
    }
    this.initialized = false;
  }

  /**
   * Porta de entrada do recurso: só vale no iOS rodando em navegador, onde existe o swipe-back
   * nativo. App nativo (Cordova) e PWA instalado na tela de início não têm o gesto do navegador —
   * ali bloquear a borda só tiraria rolagem e toque sem resolver nada.
   */
  canSetupIosSwipeBack(): boolean {
    if (this.platform.is('cordova')) {
      return false;
    }
    return this.isIos() && !this.isStandalone();
  }

  /**
   * Detecção própria em vez de `platform.is('ios')`: o iPadOS 13+ se identifica como Mac no user
   * agent, e o desempate é o suporte a toque, que o Safari de macOS não tem.
   */
  private isIos(): boolean {
    const userAgent: string = window.navigator.userAgent;
    if (/iPad|iPhone|iPod/.test(userAgent)) {
      return true;
    }
    return userAgent.indexOf('Macintosh') > -1 && window.navigator.maxTouchPoints > 1;
  }

  /** App aberto pela tela de início (PWA instalado): sem barra do navegador, sem gesto nativo. */
  private isStandalone(): boolean {
    if ((window.navigator as any).standalone === true) {
      return true;
    }
    return window.matchMedia && window.matchMedia('(display-mode: standalone)').matches;
  }

  /**
   * Impede que o encadeamento de rolagem horizontal (carrosséis, tabelas de dashboard) chegue ao
   * navegador e vire navegação de histórico. Não substitui o guarda de toque — é a camada barata
   * que resolve o caso do scroll que "estoura" para a borda.
   */
  private injectStyles(): void {
    const style: HTMLStyleElement = document.createElement('style');
    style.innerHTML = `
			html, body {
				overscroll-behavior-x: contain;
			}
		`;
    document.head.appendChild(style);
    this.teardownCallbacks.push(() => style.remove());
  }

  /**
   * Acompanha o gesto inteiro, do touchstart ao touchend, para poder distinguir arrasto de toque
   * simples — só assim o preventDefault do início pode ser compensado com o clique reemitido.
   */
  private setupEdgeTouchGuard(): void {
    let blocking: boolean = false;
    let startX: number = 0;
    let startY: number = 0;
    let startTime: number = 0;
    let maxDelta: number = 0;
    let startTarget: HTMLElement | null = null;
    let inactivityTimeout: any = null;

    const reset = (): void => {
      blocking = false;
      startTarget = null;
      maxDelta = 0;
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
        inactivityTimeout = null;
      }
    };

    // Renovado a cada evento: solta o estado só depois de o dedo ficar parado (ou de o touchend
    // se perder) pelo tempo do timeout, para não largar um arrasto lento no meio.
    const scheduleInactivityReset = (): void => {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout);
      }
      inactivityTimeout = setTimeout(reset, GESTURE_INACTIVITY_TIMEOUT_MS);
    };

    const onTouchStart = (event: TouchEvent): void => {
      reset();
      // Mais de um dedo é pinça/multitoque, nunca o swipe de borda.
      if (!event.touches || event.touches.length !== 1) {
        return;
      }
      const touch: Touch = event.touches[0];
      const nearLeftEdge: boolean = touch.clientX <= EDGE_BLOCK_ZONE_PX;
      const nearRightEdge: boolean = touch.clientX >= window.innerWidth - EDGE_BLOCK_ZONE_PX;
      if (!nearLeftEdge && !nearRightEdge) {
        return;
      }
      const target: HTMLElement = event.target as HTMLElement;
      if (this.ownsHorizontalGesture(target)) {
        return;
      }

      startX = touch.clientX;
      startY = touch.clientY;
      startTime = Date.now();
      startTarget = target;
      blocking = true;
      // É esta chamada que impede o navegador de iniciar o swipe nativo.
      event.preventDefault();
      scheduleInactivityReset();
    };

    const onTouchMove = (event: TouchEvent): void => {
      if (!blocking) {
        return;
      }
      const touch: Touch | null =
        event.touches && event.touches.length > 0 ? event.touches[0] : null;
      if (touch) {
        maxDelta = Math.max(
          maxDelta,
          Math.abs(touch.clientX - startX),
          Math.abs(touch.clientY - startY)
        );
      }
      // Segue prevenindo o gesto inteiro: o navegador reavalia o swipe nativo a cada movimento.
      event.preventDefault();
      scheduleInactivityReset();
    };

    const onTouchEnd = (event: TouchEvent): void => {
      if (!blocking) {
        return;
      }
      event.preventDefault();
      const isTap: boolean =
        maxDelta <= TAP_SLOP_PX && Date.now() - startTime <= TAP_MAX_DURATION_MS;
      const target: HTMLElement | null = startTarget;
      reset();
      if (isTap) {
        this.replayTap(target);
      }
    };

    const options: any = { passive: false, capture: true };
    document.addEventListener('touchstart', onTouchStart, options);
    document.addEventListener('touchmove', onTouchMove, options);
    document.addEventListener('touchend', onTouchEnd, options);
    document.addEventListener('touchcancel', reset, options);

    this.teardownCallbacks.push(() => {
      document.removeEventListener('touchstart', onTouchStart, options);
      document.removeEventListener('touchmove', onTouchMove, options);
      document.removeEventListener('touchend', onTouchEnd, options);
      document.removeEventListener('touchcancel', reset, options);
      reset();
    });
  }

  private ownsHorizontalGesture(target: HTMLElement | null): boolean {
    if (!target || !target.closest) {
      return false;
    }
    return target.closest(GESTURE_OWNER_SELECTOR) != null;
  }

  /**
   * Devolve o clique que o preventDefault do touchstart tirou. Reemitido no alvo original, sobe
   * pela árvore normalmente e aciona os mesmos handlers de sempre (`(click)`, `ion-item`, botões
   * do Ionic). Não há risco de clique duplicado: o WebKit não gera o clique sintético de um
   * touchstart cancelado.
   */
  private replayTap(target: HTMLElement | null): void {
    if (!target || !document.contains(target)) {
      return;
    }
    try {
      const focusable: HTMLElement | null =
        target.matches && target.matches(FOCUSABLE_SELECTOR)
          ? target
          : (target.querySelector(FOCUSABLE_SELECTOR) as HTMLElement);
      if (focusable && focusable.focus) {
        focusable.focus();
      }
      if (target.click) {
        target.click();
      }
    } catch (error) {
      console.error(error);
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/ios-swipe-back.service.ts